import { lazy, Suspense, useEffect, useState } from 'react';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { IconButton } from '@components/icon-button';
import { IconArrowDown } from '@components/icons/arrow-down';
import { IconArrowUp } from '@components/icons/arrow-up';
import { IconBuilder } from '@components/icons/builder';
import { IconChevronDownSmall } from '@components/icons/chevron-down-small';
import { Shimmer } from '@components/shimmer';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { seconds } from '@utils/milliseconds';

const MarkdownText = lazy(() => import('@components/markdown-text'));

const ROTATING_KEYS = ['working', 'thinking', 'processing', 'cooking'] as const;
const ROTATING_INTERVAL = seconds(15);
const TEXT_TRANSITION_MS = 160;

type ThinkingMessageProps = {
  text?: string;
  timeTakenSeconds?: number;
  isLive?: boolean;
  liveText?: string;
};

export const ThinkingMessage = ({
  text,
  timeTakenSeconds,
  isLive,
  liveText,
}: ThinkingMessageProps) => {
  if (isLive) {
    return <LiveThinking liveText={liveText} />;
  }

  return <CompletedThinking text={text ?? ''} timeTakenSeconds={timeTakenSeconds ?? 0} />;
};

const LiveThinking = ({ liveText }: { liveText?: string }) => {
  const { t } = useScopedTranslate(locale);
  const [textIndex, setTextIndex] = useState(0);
  const [displayedKey, setDisplayedKey] = useState<(typeof ROTATING_KEYS)[number]>(
    ROTATING_KEYS[0],
  );
  const [isTextVisible, setIsTextVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const hasThinking = Boolean(liveText?.trim());

  useEffect(() => {
    setTextIndex(0);
    const interval = setInterval(() => {
      setTextIndex((i) => (i + 1) % ROTATING_KEYS.length);
    }, ROTATING_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const nextKey = ROTATING_KEYS[textIndex];
    if (nextKey === displayedKey) {
      setIsTextVisible(true);
      return;
    }

    setIsTextVisible(false);
    const timeout = setTimeout(() => {
      setDisplayedKey(nextKey);
      setIsTextVisible(true);
    }, TEXT_TRANSITION_MS);

    return () => clearTimeout(timeout);
  }, [displayedKey, textIndex]);

  return (
    <div style={{ padding: `${pxToRem(8)} 0` }}>
      <LiveBorder>
        <ThinkingContentBox>
          <Stack orientation="horizontal" justify="space-between" align="center">
            <Stack orientation="horizontal" justify="center" align="center" space={2}>
              <IconBuilder color="aiGradientTeal100" />
              <TransitioningShimmer
                shimmerSpeed={50}
                shimmerWidth={4}
                shimmerDelay={0.2}
                aiGradient
                font="semibold12"
                text={t(displayedKey)}
                $isTextVisible={isTextVisible}
              />
            </Stack>
            {hasThinking && (
              <IconButton
                icon={
                  isExpanded ? (
                    <IconArrowUp color="textSecondary" size={16} />
                  ) : (
                    <IconArrowDown color="textSecondary" size={16} />
                  )
                }
                onClick={() => setIsExpanded((open) => !open)}
                aria-expanded={isExpanded}
                variant="ghost-white"
                size="sm"
              />
            )}
          </Stack>
          <Collapse isCollapsed={!hasThinking || !isExpanded} overflowType="visible">
            <div style={{ paddingTop: pxToRem(10) }}>
              <Suspense
                fallback={<Text color="textHelper" font="regular14" text={liveText ?? ''} />}
              >
                <MarkdownText text={liveText ?? ''} color="textHelper" />
              </Suspense>
            </div>
          </Collapse>
        </ThinkingContentBox>
      </LiveBorder>
    </div>
  );
};

const CompletedThinking = ({
  text,
  timeTakenSeconds,
}: {
  text: string;
  timeTakenSeconds: number;
}) => {
  const { t } = useScopedTranslate(locale);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div style={{ width: '100%' }}>
      <Stack orientation="horizontal" align="center" justify="center" space={1}>
        <Text
          color="textHelper"
          font="regular12"
          text={t('workedFor', { duration: formatDuration(timeTakenSeconds) })}
        />
        <IconButton
          icon={
            <ChevronIcon $isExpanded={isExpanded}>
              <IconChevronDownSmall color="textHelper" size={16} />
            </ChevronIcon>
          }
          onClick={() => setIsExpanded((open) => !open)}
          aria-expanded={isExpanded}
          variant="ghost-white"
          size="sm"
        />
      </Stack>
      <Collapse isCollapsed={!isExpanded} overflowType="visible">
        <ThinkingContentBox style={{ marginTop: pxToRem(8) }}>
          <Suspense fallback={<Text color="textHelper" font="regular14" text={text} />}>
            <MarkdownText text={text} color="textHelper" />
          </Suspense>
        </ThinkingContentBox>
      </Collapse>
    </div>
  );
};

const formatDuration = (totalSeconds: number) => {
  if (totalSeconds < 1) {
    return 'less than a second';
  }

  if (totalSeconds < 60) {
    const s = Math.round(totalSeconds);
    return `${s} second${s === 1 ? '' : 's'}`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const s = Math.round(totalSeconds % 60);

  if (s === 0) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  return `${minutes} minute${minutes === 1 ? '' : 's'} ${s} second${s === 1 ? '' : 's'}`;
};
const LiveBorder = styled.div({
  width: '100%',
  borderRadius: pxToRem(8),
  padding: pxToRem(1),
  background: theme.gradient.aiSurface30,
});

const ThinkingContentBox = styled.div({
  width: '100%',
  borderRadius: pxToRem(8),
  padding: pxToRem(12),
  background: theme.color.backgroundLightDark,
});

const TransitioningShimmer = styled(Shimmer)<{ $isTextVisible: boolean }>(({ $isTextVisible }) => ({
  display: 'inline-block',
  opacity: $isTextVisible ? 1 : 0,
  transform: `translateY(${!$isTextVisible ? pxToRem(2) : 0})`,
  transition: `opacity ${TEXT_TRANSITION_MS}ms ease, transform ${TEXT_TRANSITION_MS}ms ease`,
  '@media (prefers-reduced-motion: reduce)': {
    opacity: 1,
    transform: 'none',
    transition: 'none',
  },
}));

const ChevronIcon = styled.span<{ $isExpanded: boolean }>(({ $isExpanded }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  transition: 'transform 200ms ease',
  transform: $isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
}));

const locale = {
  en: {
    working: 'Working...',
    thinking: 'Thinking...',
    processing: 'Processing...',
    cooking: 'Cooking...',
    workedFor: 'Worked for {duration}',
  },
};
